import { EntityState } from '@ngrx/entity';

/**
 * Base state interface for paginated state models
 * @template TData The data type for items in the state model (must have id)
 */
export interface PaginatedStateModel<TData extends { id: string }> {
  // Data (normalized with EntityAdapter)
  items: EntityState<TData>;

  // Memory for deferred loading. Can be disabled by config
  deferredAttributeMemory: Record<string, Partial<TData>>;

  // Pagination
  nextToken?: TData;
  hasMoreItems: boolean;
  previousPageTokens: TData[];
  pageSize: number;

  // Search and count
  searchTerm?: string;
  totalCount: number;

  // Loading states
  loading: boolean;
  counting: boolean;
  searching: boolean;
  deleting: boolean;

  // Error handling
  error: string | undefined;
}

/**
 * Loading mode for paginated stores
 */
export enum PaginatedStateLoadingMode {
  /**
   * Replace items on each page load (traditional pagination)
   */
  PAGINATED = 'paginated',

  /**
   * Append new items to existing list (infinite scroll)
   */
  CONTINUOUS = 'continuous',
}

/**
 * Configuration options for paginated stores
 */
export interface PaginatedStateConfig {
  /**
   * Initial page size for pagination
   * @default 10
   */
  initialPageSize: number;

  /**
   * Maximum number of concurrent count queries for deferred loading
   * @default 2
   */
  maxConcurrentCountQueries: number;

  /**
   * Loading mode for items
   * @default PaginatedStateLoadingMode.PAGINATED
   */
  loadingMode: PaginatedStateLoadingMode;

  /**
   * Whether to use memory for deferred attributes
   * @default true
   */
  useMemory: boolean;
}

/**
 * Default configuration values
 */
export const DEFAULT_PAGINATED_STATE_CONFIG: Required<PaginatedStateConfig> = {
  initialPageSize: 10,
  maxConcurrentCountQueries: 2,
  loadingMode: PaginatedStateLoadingMode.PAGINATED,
  useMemory: true,
};
